import React, { ReactElement, useState } from "react";
import styles from "./TimetableBlock.module.scss";
import moment from 'moment';
import { IEvent, IEventInfo, IEventInfoHoursGroups } from "@common/redux/types/event";
import { IBuyButtonEventExtraProps } from "@common/redux/types/common";
import { ShowMoreIcon } from "@kursk/components/ui/icons";
import classNames from "classnames";
import { YMap } from "@kursk/components/ui/YMap/YMap";
import { getAddress } from "@kursk/utils/getAddress";
import Link from 'next/link'
import { getFullDay } from "@kursk/utils/getFullDay";
import { BuyButton } from "@kursk/components/ui/BuyButton/BuyButton";
import { getComment } from "@kursk/utils/getComment";
import { ITour } from "@common/redux/types/tour";
import { regionContent } from "@kursk/content/regionContent";
import { BookingButton } from "../BookingButton/BookingButton";

interface IProps {
  entity: IEvent | ITour;
  mode: 'header' | 'schedule';
  forRef?: any;
  info: IEventInfo;
  defaultDate?: number;
  showBuyButton?: boolean;
  index?: number;
  entityType: 'events' | 'tours';
  bookingOnly?: boolean;
  enabledDays?: number[];
}

export const TimetableBlock = ({
  mode,
  forRef,
  info,
  entity,
  defaultDate,
  showBuyButton,
  index,
  entityType,
  bookingOnly
}: IProps): ReactElement => {
  const [isShownMap, setIsShownMap] = useState(false);
  const isFinished = info.periodEnd < moment().valueOf();
  const isSeance = entity?.type === 'seances';

  const { periodEnd, periodStart } = info;
  const hoursGroups = info.hoursGroups;
  const venue = info.venue;
  const oneDay = hoursGroups && hoursGroups.length > 0 ? getFullDay(hoursGroups[0].days) : false;
  let currentStart = periodStart;
  if (!isSeance) {
    const now = moment().valueOf();
    currentStart = periodStart < now && !isFinished ? now : periodStart;
  }
  const dayStart = moment(currentStart).format('DD MMMM').split(' ')[0];
  const monthStart = moment(currentStart).format('DD MMMM').split(' ')[1];
  const yearStart = moment(currentStart).format('YYYY');
  const dayEnd = moment(periodEnd).format('DD MMMM').split(' ')[0];
  const monthEnd = moment(periodEnd).format('DD MMMM').split(' ')[1];
  const yearEnd = moment(periodEnd).format('YYYY');
  const withPlace = Boolean(venue?.place);
  const address = getAddress(venue?.place?.address || venue?.location?.address)
    + (withPlace ? '' : getComment(venue?.location?.address));

  const showMore = () => setIsShownMap(!isShownMap);
  const scrollToTimetables = () => window.scrollTo({ top: forRef.current.offsetTop - 100, behavior: 'smooth' });

  const eventExtraProps = {
    eventType: entity?.type,
    [`${entity?.type}Id`]: info._id,
  } as IBuyButtonEventExtraProps;
  return (
    <>
      <div className={classNames(
        styles.timetableBlock,
        mode === 'schedule' && styles.single,
        isSeance && styles.seances,
        (showBuyButton || bookingOnly) && styles.withButton,
      )}
      >
        <div className={styles.timetableBlock_dateWrapper}>
          {isSeance ? (
            <div className={styles.timetableBlock_dateWrapper__dateStart}>
              <div className={styles.timetableBlock_dateWrapper__dayStart}>
                {dayStart}
              </div>
              <div className={styles.timetableBlock_dateWrapper__monthYearStart}>
                <div className={styles.timetableBlock_dateWrapper__monthStart}>
                  {`${monthStart} ${yearStart}`}
                </div>
                <div className={styles.timetableBlock_dateWrapper__yearStart}>
                  {oneDay}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.timetableBlock_dateWrapper__date}>
              <div className={styles.timetableBlock_dateWrapper__dateStartAndEnd}>
                <div className={styles.timetableBlock_dateWrapper__dateStart}>
                  <div className={styles.timetableBlock_dateWrapper__dayStart}>
                    {dayStart}
                  </div>
                  <div className={styles.timetableBlock_dateWrapper__monthYearStart}>
                    <div className={styles.timetableBlock_dateWrapper__monthStart}>
                      {monthStart}
                    </div>
                    <div className={styles.timetableBlock_dateWrapper__yearStart}>
                      {yearStart}
                    </div>
                  </div>
                </div>
                <div className={styles.timetableBlock_dateWrapper__dash}>&mdash;</div>
                <div className={styles.timetableBlock_dateWrapper__dateEnd}>
                  <div className={styles.timetableBlock_dateWrapper__dayEnd}>
                    {dayEnd}
                  </div>
                  <div className={styles.timetableBlock_dateWrapper__monthYearEnd}>
                    <div className={styles.timetableBlock_dateWrapper__monthEnd}>
                      {monthEnd}
                    </div>
                    <div className={styles.timetableBlock_dateWrapper__yearEnd}>
                      {yearEnd}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.timetableBlock_timeWrapper}>
          {isFinished ? (
            <div className={styles.timetableBlock_timeWrapper__time}>
              <span>
                {entityType==='tours' ? 'Тур прошел' : 'Событие прошло'}
              </span>
            </div>
          ) : (
            <>
              {isSeance ? (
                <div className={styles.timetableBlock_timeWrapper__time}>
                  <span>
                    {
                      entityType === 'tours' ?
                        moment.tz(currentStart, regionContent.timezone).format('HH:mm') :
                        hoursGroups[0].times
                    }
                  </span>
                </div>
              ) : (
                <div className={styles.timetableBlock_timeWrapper__timesList}>
                  {hoursGroups.map((hourGr: IEventInfoHoursGroups, index: number) => (
                    <div className={styles.timetableBlock_timeWrapper__time} key={index}>
                      <span className={styles.timetableBlock_timeWrapper__time_days}>
                        {hourGr.days}
                      </span>
                      <span className={styles.timetableBlock_timeWrapper__time_time}>
                        {hourGr.times}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className={classNames(styles.timetableBlock_placeWrapper, withPlace && styles.withPlaceName)}>
          <div className={styles.timetableBlock_placeWrapper_place}>
            {withPlace && (
              <div className={styles.timetableBlock_placeWrapper__name}>
                <Link href={`/places/${venue.place._id}/${venue.place.sysName}`}>
                  {venue.place?.name}
                </Link>
              </div>
            )}
            <button
              className={styles.timetableBlock_placeWrapper__button}
              onClick={() => showMore()}
            >
              <div className={styles.timetableBlock_placeWrapper__address}>
                {address}
                <ShowMoreIcon
                  classes={classNames([styles.showMoreIcon, isShownMap && styles.rotate])}
                  width={12}
                  height={5}
                />
              </div>
            </button>
          </div>
        </div>
        {(showBuyButton || bookingOnly )&& (
          <div className={styles.timetableBlock_buttonWrapper}>
            <div className={styles.timetableBlock_buttonWrapper__payWrapper}>
              <div className={styles.timetableBlock_buttonWrapper__payInner}>
                {bookingOnly ?
                  <BookingButton
                    defaultSeance={info._id}
                    defaultDate={defaultDate}
                    entityType="tours"
                    entity={entity as ITour}
                  /> :
                  <BuyButton
                    entityType={entityType}
                    venueId={venue.place?._id.toString()}
                    entityId={entity._id}
                    defaultDate={defaultDate}
                    eventExtraProps={eventExtraProps}
                    position='schedule'
                    dataTestId={`buy-button-schedule-${index}`}
                  />
                }
              </div>
            </div>
          </div>
        )}
        {isShownMap && (
          <div className={styles.timetableBlock_mapWrapper}>
            <div className={styles.timetableBlock_mapWrapper__map}>
              <YMap
                classes={styles.timetableBlock_mapWrapper__mapInner}
                mapPosition={venue.place?.mapPosition || venue.location?.mapPosition}
                placeName={venue.place?.name || getAddress(venue.location?.address)}
              />
            </div>
          </div>
        )}
      </div>
      {entity && entity.actualSeances.all.length > 1 && mode === 'header' && (
        <div onClick={() => scrollToTimetables()} className={styles.showMoreButton}>
          ПОСМОТРЕТЬ ЕЩЁ
        </div>
      )}
    </>
  )
}
